import Checkbox from 'components/Checkbox/checkbox'
import { useDataTableContext } from 'components/DataTable/DataTableContext'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { cn } from 'components/UI/cn'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { EmailThread } from 'models/emailIntegration'
import React, { useCallback, useEffect, useRef } from 'react'
import {
  PiArrowCounterClockwise,
  PiCaretDown,
  PiCaretLeft,
  PiCaretRight,
  PiPath,
  PiToolbox,
} from 'react-icons/pi'
import { getDropdownZIndex } from 'styles/z-index'

interface InboxActionBarProps {
  page: number
  pageSize: number
  total: number
  threads: EmailThread[]
  onPageChange: (page: number) => void
  onRefresh: () => void
}

/** Toolbar with select checkbox, refresh, actions dropdown, and pagination. */
export function InboxActionBar({
  page,
  pageSize,
  total,
  threads,
  onPageChange,
  onRefresh,
}: InboxActionBarProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const {
    state: { isAllRowsSelected, totalSelectedRows, totalRowsInPage },
    methods: { setRowSelection, setIsAllRowsSelected },
  } = useDataTableContext()

  const checkboxRef = useRef<HTMLInputElement>(null!)

  // Sync indeterminate state
  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate =
        !isAllRowsSelected && totalSelectedRows > 0
    }
  }, [totalSelectedRows, isAllRowsSelected])

  const selectAll = useCallback(() => {
    setIsAllRowsSelected(true)
    // Select all rows on current page
    const selection: Record<string, boolean> = {}
    threads.forEach((_, i) => {
      selection[i] = true
    })
    setRowSelection(selection)
  }, [threads, setIsAllRowsSelected, setRowSelection])

  const selectNone = useCallback(() => {
    setIsAllRowsSelected(false)
    setRowSelection({})
  }, [setIsAllRowsSelected, setRowSelection])

  const selectAllOnPage = useCallback(() => {
    setIsAllRowsSelected(false)
    const selection: Record<string, boolean> = {}
    threads.forEach((_, i) => {
      selection[i] = true
    })
    setRowSelection(selection)
  }, [threads, setIsAllRowsSelected, setRowSelection])

  const handleCheckboxChange = useCallback(() => {
    if (isAllRowsSelected || totalSelectedRows > 0) {
      selectNone()
    } else {
      selectAll()
    }
  }, [isAllRowsSelected, totalSelectedRows, selectNone, selectAll])

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)
  const hasPrev = page > 1
  const hasNext = end < total

  return (
    <div className="flex items-center justify-between h-[48px] px-3.5 border-b border-[#EDEFF1]">
      {/* Left: Checkbox dropdown + Refresh + Actions */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          {/* Bulk select checkbox + dropdown */}
          <div className="flex items-center gap-3 h-8 ml-1">
            <Checkbox
              ref={checkboxRef}
              checked={isAllRowsSelected}
              onChange={handleCheckboxChange}
              onClick={(e) => e.stopPropagation()}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-center size-4 text-[#6E6E6E] hover:text-[#191D23] transition-colors"
                >
                  <PiCaretDown className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                style={{ minWidth: 160, zIndex }}
                className="bg-white border-[#8EA69B]"
              >
                <DropdownMenuCheckboxItem
                  checked={isAllRowsSelected}
                  onSelect={selectAll}
                >
                  All ({total.toLocaleString()})
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={
                    !isAllRowsSelected &&
                    totalSelectedRows > 0 &&
                    totalSelectedRows === totalRowsInPage
                  }
                  onSelect={selectAllOnPage}
                >
                  All on page ({threads.length})
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={totalSelectedRows === 0}
                  onSelect={selectNone}
                >
                  None
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Refresh */}
          <button
            type="button"
            onClick={onRefresh}
            className="flex items-center justify-center size-8 rounded hover:bg-[#F3F3F3] text-[#6E6E6E] transition-colors"
            title="Refresh"
          >
            <PiArrowCounterClockwise className="size-[22px]" />
          </button>
        </div>

        {/* Actions dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-1.5 h-8 px-1.5 rounded hover:bg-[#F3F3F3] text-sm font-medium text-[#6E6E6E] hover:text-[#191D23] transition-colors"
            >
              <PiToolbox className="size-6" />
              <span>Actions</span>
              <PiCaretDown className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            style={{ minWidth: 200, zIndex }}
            className="bg-white border-[#8EA69B]"
          >
            <DropdownMenuItem
              disabled
              className="flex items-center gap-2 opacity-50 cursor-not-allowed"
            >
              <PiPath className="size-4 flex-shrink-0 text-gray-500" />
              <span>Assign all to deal</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Right: Pagination */}
      <div className="flex items-center gap-[15px] h-[48px] text-sm text-[#6E6E6E]">
        <span className="py-2">
          {total > 0 ? `${start}\u2013${end}` : '0'} of {total.toLocaleString()}
        </span>
        <button
          type="button"
          disabled={!hasPrev}
          onClick={() => onPageChange(page - 1)}
          className={cn(
            'p-1 rounded transition-colors',
            hasPrev
              ? 'hover:bg-[#F3F3F3] text-[#6E6E6E]'
              : 'text-[#D0D0D0] cursor-not-allowed'
          )}
        >
          <PiCaretLeft className="size-[22px]" />
        </button>
        <button
          type="button"
          disabled={!hasNext}
          onClick={() => onPageChange(page + 1)}
          className={cn(
            'p-1 rounded transition-colors',
            hasNext
              ? 'hover:bg-[#F3F3F3] text-[#6E6E6E]'
              : 'text-[#D0D0D0] cursor-not-allowed'
          )}
        >
          <PiCaretRight className="size-[22px]" />
        </button>
      </div>
    </div>
  )
}
